import { CdkDragDrop, CdkDragEnd, CdkDragStart, DragRef } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef } from '@angular/core';
import { DialogService } from '../services/actions/dialog.service';
import { IDragable } from './interfaces/dragable.interface';
import { SelectionContext } from './selection-context';

export class DragContext<T> {
    /** Indique qu'une opération de 'glisser (drag) d'un dossier est en cours. */
    public isDragging: boolean = false;

    /** Reference de l'élément glissé. */
    public draggingRef: DragRef = null;

    /** Liste des description des dossiers déplacés lors d'un drag&drop qui doivent être affichés dans la preview du drag&drop*/
    public previews: string[] = [];

    /** Liste des index des dossiers ayant été déposés par drag&drop (Utilisé pour maintenir les dossiers cachés jusqu'à la fin de l'opération de drop) */
    public droppedFolderIndices: number[] = [];

    /**
     * Constructeur.
     * @param cdRef : Detecteur de changement.
     */
    constructor(
        private cdRef: ChangeDetectorRef,
        private selectionContext: SelectionContext,
        private souceComponent: IDragable<T>,
        private dialogService: DialogService
    ) {}

    /**
     * Levé lorsque une opération de glisser (drag) d'un dossier commence.
     * @param event : Événement de glisser (drag) d'un dossier.
     */
    public onDragStarted(event: CdkDragStart, folder: T, index: number): void {
        // Enrichit l'événement de glisser (drag) avec le tableau des indices des éléments selectionnés, le tableau des objets métiers associés à chaque élément sélectionné, et la source de l'evenment.
        this.isDragging = true;
        this.draggingRef = event.source._dragRef; // Stocke la reference de l'élément glissé.

        if (this.selectionContext.selections.length == 0) {
            // Si la selection est vide, alors l'élément glissé est l'unique élément de la selection.
            this.selectionContext.selections = [index];
        }

        this.previews = this.souceComponent.BuildPreviews();

        const indices = this.selectionContext.selections.length ? this.selectionContext.selections : [index]; // Récupère les indices des éléments selectionnés si plusiers sont sélectionnés sinon l'index de l'élément courrant.

        event.source.data = {
            folder: folder,
            type: 'Folder',
            indices,
            folders: this.souceComponent.getDragedRecords(indices),
            source: this,
        };

        this.cdRef.detectChanges();
    }

    /**
     * Levé lorsque une opération de 'glisser' (drag) d'un dossier se termine.
     * @param event : Événement de 'glisser' (drag) d'un dossier.
     */
    public onDragEnded(event: CdkDragEnd, folder: T) {
        event.source.data.position = event.dropPoint;

        this.droppedFolderIndices = event.source.data.indices;
        this.isDragging = false;
        this.draggingRef = null;

        this.cdRef.detectChanges();
    }

    /**
     * Emis lorsque l'utilisateur dépose un élément dans la liste des dossiers
     * @param event : Événement de glisser/deposer
     */
    public onDrop(event: CdkDragDrop<T>): void {
        let displayedFolders = this.souceComponent.getRecords();

        if (Number.isNaN(event.dropPoint.x) && Number.isNaN(event.dropPoint.y)) {
            // Cas d'un drag&drop annulé par la touche ECHAP
            this.clearDragAndDropContext();
            return;
        }

        if (event.item.data.type === 'Folder' && event.container === event.previousContainer) {
            // Si l'élément "déposé" est de type Folder et que l'élément déposé provient de la liste des dossiers alors on declenche la fusion des dossiers.

            // Récupération de l'élément HTML sur lequel l'utilisateur à déposé le dossier.
            let dropElement = document.elementFromPoint(event.dropPoint.x, event.dropPoint.y);

            // Récupération de la table HTML correspondant à la liste des dossiers.
            let table = event.previousContainer.element.nativeElement as HTMLTableElement;

            // Détermination de l'index du dossier de destination
            let destinationFolderIndex: number = -1;

            let insideFolderTable: boolean = false;

            for (let i = 0; i < table.rows.length; i++) {
                if (table.rows[i].classList.contains('element-row') && !table.rows[i].classList.contains('hidden')) {
                    destinationFolderIndex += 1;
                }

                // On parcours toutes les lignes de la table à la recherche de la ligne contenant l'élément HTML sur lequel l'utilisateur à déposé le dossier.
                if (table.rows[i].contains(dropElement)) {
                    insideFolderTable = true;
                    break;
                }
            }

            this.dialogService.openConfirmMerge(event.item.data.folders, displayedFolders[destinationFolderIndex]).subscribe((isConnfirm) => {
                if (isConnfirm) {
                    if (insideFolderTable) {
                        // Récupération du dossier de destination.
                        let destinationFolder = displayedFolders[destinationFolderIndex];

                        // Itère sur chaque dossier sélectionné et le fusionne au dossier destination.
                        event.item.data['folders'].forEach((folder) => {
                            this.souceComponent.executeOnDropAction(folder, destinationFolder);
                        });

                        // Vide la selection.
                        this.selectionContext.clearSelection();
                    } else {
                        this.clearDragAndDropContext();
                    }
                } else {
                    // Cas d'un drag&drop annulé par la boite de dialogue
                    this.clearDragAndDropContext();
                }
            });
        }
    }

    /**
     * Nettoie le contexte relatif au Drag&Drop.
     */
    public clearDragAndDropContext() {
        this.droppedFolderIndices = [];
    }

    /**
     * Indique si l'élément a été déposé par drag&drop.
     * @param index : index de l'élément à tester
     * @returns True si l'élément a été déposé, ou false autrement.
     */
    public isDropped(index: number): boolean {
        return this.droppedFolderIndices.indexOf(index) >= 0;
    }

    /**
     * Traite les événements clavier de type KeyDown sur le document relatif au drag&drop.
     * @param event Événement du clavier.
     */
    public handleKeyboardEvent(event: KeyboardEvent) {
        if (event.key === 'Escape' && this.draggingRef) {
            // Si la touche ECHAP est pressée et qu'un glisser/deposer est en cours...
            this.draggingRef.reset();
            // Emet un événement mouseup pour terminer l'opération de glisser/deposer est en cours.
            document.dispatchEvent(new Event('mouseup'));
            event.cancelBubble = true;
        }
    }
}
